import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from '@mui/material';

interface RenameFolderDialogProps {
  open: boolean;
  onClose: () => void;
  currentName: string;
  onSuccess?: (newName: string) => void;
}

export const RenameFolderDialog: React.FC<RenameFolderDialogProps> = ({
  open,
  onClose,
  currentName,
  onSuccess,
}) => {
  const [folderName, setFolderName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  // Reset folder name when dialog opens with new currentName
  useEffect(() => {
    if (open) {
      setFolderName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  const trimmedName = useMemo(() => folderName.trim(), [folderName]);
  const charCount = useMemo(() => folderName.length, [folderName]);
  const isOverLimit = useMemo(() => charCount > 255, [charCount]);
  const isValid = useMemo(
    () => trimmedName.length > 0 && !isOverLimit,
    [trimmedName, isOverLimit]
  );
  const hasChanged = useMemo(
    () => trimmedName !== currentName.trim(),
    [trimmedName, currentName]
  );

  const handleClose = useCallback(() => {
    setFolderName(currentName);
    setError(null);
    onClose();
  }, [currentName, onClose]);

  const handleRename = useCallback(() => {
    if (!isValid) {
      setError('Please enter a valid folder name (1-255 characters)');
      return;
    }

    if (!hasChanged) {
      handleClose();
      return;
    }

    onSuccess?.(trimmedName);
    handleClose();
  }, [isValid, hasChanged, onSuccess, trimmedName, handleClose]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isValid && hasChanged) {
        handleRename();
      }
    },
    [isValid, hasChanged, handleRename]
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename Folder</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          label="Folder Name"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          error={isOverLimit}
          helperText={
            isOverLimit
              ? `${charCount - 255} characters over limit`
              : `${charCount}/255 characters`
          }
          fullWidth
          margin="normal"
          onKeyPress={handleKeyPress}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleRename}
          variant="contained"
          disabled={!isValid || !hasChanged}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
